import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { OfflineProvider } from "@/context/OfflineContext";
import { registerServiceWorker } from "@/lib/swRegister";

// Global freshness guarantee: after ANY successful mutation (create/edit/delete/approve/…),
// mark every query stale so mounted tables/cards/dashboards/dropdowns refetch automatically —
// no manual page refresh. invalidateQueries only actively refetches CURRENTLY MOUNTED queries
// (a handful per screen), so this stays cheap. Opt out per-mutation with meta:{noInvalidate:true}.
const mutationCache = new MutationCache({
  onSuccess: (_data, _vars, _ctx, mutation) => {
    if (mutation?.meta?.noInvalidate) return;
    queryClient.invalidateQueries();
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,      // 5 min — revisiting a page within the window is instant (no refetch)
      gcTime: 30 * 60_000,        // keep cached data 30 min so back-navigation is snappy
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
// NOTE: React.StrictMode intentionally double-invokes effects/renders in DEVELOPMENT only, which in
// this large app doubles every mount-time API call and re-renders heavy screens twice — a major cause
// of the slow first-load/module-load felt in the dev/preview server. It has ZERO effect on the
// production build (double-invoke is dev-only), so removing it speeds the preview with no prod change.
root.render(
  <QueryClientProvider client={queryClient}>
    <OfflineProvider>
      <App />
    </OfflineProvider>
  </QueryClientProvider>,
);

registerServiceWorker();
